/**
 * Choosing roles, each at a scope.
 *
 * The pair is the unit and the control makes that visible: a row is "this role,
 * over this much". Splitting them into two lists is how somebody ends up with a
 * role they meant to confine to one department applying to the whole
 * organization — the scope is the half nobody remembers to set.
 *
 * Only roles of the matching `kind` are offered. A staff role handed to a
 * client user would grant the staff capability behind `support.ticket.view`,
 * which means "every client's tickets" rather than "mine"; the server refuses
 * it, and this does not offer it.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Field, Select, api, cn } from '@connect/web-shared';

export interface Assignment {
  role_id: string;
  scope_kind: 'organization' | 'department' | 'team' | 'own';
  department_id: string | null;
  team_id: string | null;
}

interface Role {
  public_id: string;
  name: string;
  description: string | null;
}

interface Unit {
  public_id: string;
  name: string;
}

const SCOPE_OPTIONS = [
  { value: 'organization', label: 'Everything' },
  { value: 'department', label: 'One department' },
  { value: 'team', label: 'One team' },
  { value: 'own', label: 'Only their own records' },
];

export function RoleAssignmentEditor({
  value,
  onChange,
  hint,
  disabled,
}: {
  value: Assignment[];
  onChange: (next: Assignment[]) => void;
  hint?: string;
  disabled?: boolean;
}) {
  const roles = useQuery({
    queryKey: ['iam', 'roles'],
    queryFn: () => api.get<Role[]>('/iam/roles'),
    staleTime: 5 * 60_000,
  });

  const departments = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => {
      const page = await api.page<Unit>('/hr/departments', { params: { page_size: 200 } });
      return page.rows;
    },
    // Only when a department scope is actually in play. An installation
    // without the People service has none, and asking for them would 403.
    enabled: value.some((a) => a.scope_kind === 'department'),
  });

  const teams = useQuery({
    queryKey: ['hr', 'teams'],
    queryFn: async () => {
      const page = await api.page<Unit>('/hr/teams', { params: { page_size: 200 } });
      return page.rows;
    },
    enabled: value.some((a) => a.scope_kind === 'team'),
  });

  const available = useMemo(
    () => roles.data ?? [],
    [roles.data],
  );

  const update = (index: number, patch: Partial<Assignment>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <Field
      label="Roles"
      hint={hint ?? 'A role says what they may do; the scope says over how much.'}
    >
      <div className="space-y-2">
        {value.length === 0 ? (
          <p className="rounded-control border border-dashed border-line px-3 py-3 text-xs text-faint">
            No roles yet.
          </p>
        ) : (
          value.map((row, index) => (
            <div
              key={index}
              className="grid gap-2 rounded-control border border-line p-2 sm:grid-cols-[1fr_auto_auto]"
            >
              <Select
                aria-label="Role"
                options={[
                  { value: '', label: 'Choose a role' },
                  ...available.map((role) => ({ value: role.public_id, label: role.name })),
                ]}
                value={row.role_id}
                disabled={disabled}
                onChange={(event) => update(index, { role_id: event.target.value })}
              />
              <div className="flex gap-2">
                <Select
                  aria-label="Scope"
                  options={SCOPE_OPTIONS}
                  value={row.scope_kind}
                  disabled={disabled}
                  onChange={(event) =>
                    update(index, {
                      scope_kind: event.target.value as Assignment['scope_kind'],
                      // Cleared when the scope changes. A stale department on
                      // an organization-wide assignment reads as narrower than
                      // the grant actually in force.
                      department_id: null,
                      team_id: null,
                    })
                  }
                  className={cn(row.scope_kind === 'organization' && 'text-body')}
                />
                {row.scope_kind === 'department' ? (
                  <Select
                    aria-label="Department"
                    options={[
                      { value: '', label: 'Which department?' },
                      ...(departments.data ?? []).map((d) => ({
                        value: d.public_id,
                        label: d.name,
                      })),
                    ]}
                    value={row.department_id ?? ''}
                    disabled={disabled}
                    onChange={(event) =>
                      update(index, { department_id: event.target.value || null })
                    }
                  />
                ) : null}
                {row.scope_kind === 'team' ? (
                  <Select
                    aria-label="Team"
                    options={[
                      { value: '', label: 'Which team?' },
                      ...(teams.data ?? []).map((t) => ({ value: t.public_id, label: t.name })),
                    ]}
                    value={row.team_id ?? ''}
                    disabled={disabled}
                    onChange={(event) => update(index, { team_id: event.target.value || null })}
                  />
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove this role"
                disabled={disabled}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))
        )}

        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() =>
            onChange([
              ...value,
              { role_id: '', scope_kind: 'organization', department_id: null, team_id: null },
            ])
          }
        >
          <Plus className="size-3.5" />
          Add a role
        </Button>
      </div>
    </Field>
  );
}
